import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  BrokerMessageModel,
  EntrySummaryDutiesModel,
  EntrySummaryModel,
  EntrySummaryShortModel,
  InvoiceModel,
  LineModel,
} from '@data-layer/models/entry-summaries';
import {
  BondModel,
  BondRequestModel,
  BondRequestUpdateModel,
  ForeignPortModel,
  ShipmentModel,
  ShortModel,
  UnitModel,
  ValidationMessageModel,
} from '@data-layer/models/common';
import { CompanyBondModel, CompanyModel, SelfFilerModel } from '@data-layer/models/companies';
import {
  enumChargesDistributionType,
  enumEntrySummaryDutiesPaymentType,
  enumEntrySummaryState,
  enumEntrySummarySubmissionType,
  enumEntrySummaryType,
  enumTransportationMode,
} from '@data-layer/enums/entry-summaries';
import { BehaviorSubject, combineLatest, EMPTY, forkJoin, Observable, of, ReplaySubject, timer } from 'rxjs';
import { catchError, delay, distinctUntilChanged, filter, first, map, mergeMap, switchMap, take, takeWhile, tap } from 'rxjs/operators';
import { EntityModel } from '@data-layer/models/entities';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { enumGATagEventCategory } from '@enum/enumGATagEventCategory';
import { enumGATagEventAction } from '@enum/enumGATagEventAction';
import {
  BillsRepository,
  BondRequestRepository,
  BrokerEntryRepository,
  BrokerMessagesRepository,
  CodeRepository,
  CompanyBondsRepository,
  DocumentsRepository,
  EntitiesRepository,
  EntrySummaryRepository,
  InvoicesRepository,
  LineItemsRepository,
  SelfEntryRepository,
  SelfFilerRepository,
  ShipmentRepository,
  ShipmentsV2Repository,
} from '@data-layer/repositories';
import {
  enumBondRequestState,
  enumBondSubType,
  enumBondType,
  enumIdentifierQualifier,
  enumOcrStatus,
  enumPlatformType,
  enumWeightUnit,
} from '@data-layer/enums/common';
import { DocumentCreateModel, DocumentModel } from '@data-layer/models/documents';
import { IEsValidationData } from '@interfaces/es-validation-data';
import { DocumentsService } from '@services/documents.service';
import { CompaniesService } from '@services/companies.service';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { EntrySummaryService } from '@services/entry-summary.service';
import { EntrySummaryValidationService } from '@services/entry-summary-validation.service';
import { BillItemPendingModel, BillModel, PaymentBillItemPendingModel } from '@data-layer/models/payments';
import { enumGACheckoutSteps } from '@enum/enumGACheckoutSteps';
import { DateHelper } from '@helpers/date.helper';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@data-layer/enums/documents';
import { LclEntrySummaryUpsertModel, LclIsfEsLinkModel, LCLShipmentModel, PackageModel } from '@data-layer/models/lcl';
import { enumIncotermsType } from '@data-layer/enums/lcl/enumIncotermsType';
import { DEFAULT_LONGER_POLLING_TIMEOUT, DEFAULT_MAX_POLLING_RETRY, LB_TO_KG_RATIO, USA_COUNTRY_CODE } from '@constants/constants';
import { precisionRound } from '@helpers/data-transform.helper';
import { enumPackageType } from '@data-layer/enums/lcl';
import { MarineInsuranceAvailabilityService, MarineInsuranceFacade, MarineInsuranceState } from '@shared/marine-insurance-shared/services';
import { MarineInsuranceModel } from '@data-layer/models/marine-insurance';
import { cloneDeep } from 'lodash-es';
import {
  INVOICE_MANUFACTURER_MISSING,
  INVOICE_OCR_STATE_NOT_REVIEWED,
  LINES_MISSING,
  MANUFACTURER_EXPECTED_TO_BE_CANADIAN,
} from '@constants/entity-validation-messages';
import { UtilsHelper } from '@helpers/utils-helper';
import { UserModel } from '@app/data-layer/models/user';

@Injectable({
  providedIn: 'root',
})
export class EntrySummaryStateService {
  public entrySummary$: BehaviorSubject<EntrySummaryModel> = new BehaviorSubject({} as EntrySummaryModel);
  public pendingBills$: BehaviorSubject<BillItemPendingModel[]> = new BehaviorSubject([]);
  public canceledBills$: BehaviorSubject<BillItemPendingModel[]> = new BehaviorSubject([]);
  public entryDocuments$: BehaviorSubject<DocumentModel[]> = new BehaviorSubject([]);
  public entryBlDocumentsUploaded$: BehaviorSubject<number[]> = new BehaviorSubject([]);
  public validationMessages$: BehaviorSubject<ValidationMessageModel[]> = new BehaviorSubject([]);
  public validationsList$: BehaviorSubject<{ [name: string]: boolean }> = new BehaviorSubject(null);
  public forceDataReload$: BehaviorSubject<boolean> = new BehaviorSubject(null);
  public currentOCRState$: BehaviorSubject<enumOcrStatus> = new BehaviorSubject(null);
  public currentEsDocOCRState$: BehaviorSubject<enumOcrStatus> = new BehaviorSubject(null);
  // #12298: derived signal — true while an ES document OCR scan is in progress; drives the blocking overlay on the client summary.
  public isEsDocOcrInProgress$: Observable<boolean> = this.currentEsDocOCRState$.pipe(
    map(
      (status: enumOcrStatus) =>
        status === enumOcrStatus.PROCESSING ||
        status === enumOcrStatus.CONVERTING_FILE_TO_IMAGE ||
        status === enumOcrStatus.EXTRACTING_DATA ||
        status === enumOcrStatus.CLASSIFYING_LINE_ITEMS,
    ),
    distinctUntilChanged(),
  );
  public wizardStepNext$: ReplaySubject<void> = new ReplaySubject<void>();
  public wizardStepPrev$: ReplaySubject<void> = new ReplaySubject<void>();
  public isShowPaymentBtn$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  public invoiceSaved$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  public invoiceCleared$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  public shipmentRemoved$: ReplaySubject<number> = new ReplaySubject<number>();
  public uploadDocsStripDocument$: ReplaySubject<{
    document: DocumentModel;
    shipmentId: number;
    isForceAdd?: boolean;
    isInvoiceReplace?: boolean;
  }> = new ReplaySubject<{ document: DocumentModel; shipmentId: number; docNumber?: number; isForceAdd?: boolean }>();

  constructor(
    private companiesService: CompaniesService,
    private entitiesRepository: EntitiesRepository,
    private ga: GoogleAnalyticsService,
    private companyBondsRepository: CompanyBondsRepository,
    private selfFilerRepository: SelfFilerRepository,
    private entrySummaryRepository: EntrySummaryRepository,
    private selfEntryRepository: SelfEntryRepository,
    private brokerEntryRepository: BrokerEntryRepository,
    private codeRepository: CodeRepository,
    private lineItemsRepository: LineItemsRepository,
    private shipmentsV2Repository: ShipmentsV2Repository,
    private brokerMessagesRepository: BrokerMessagesRepository,
    private entrySummaryService: EntrySummaryService,
    private entrySummaryValidationService: EntrySummaryValidationService,
    private documentsRepository: DocumentsRepository,
    private documentsService: DocumentsService,
    private invoicesRepository: InvoicesRepository,
    private billsRepository: BillsRepository,
    private bondRequestRepository: BondRequestRepository,
    private marineInsuranceAvailabilityService: MarineInsuranceAvailabilityService,
    private marineInsuranceFacade: MarineInsuranceFacade,
    private marineInsuranceState: MarineInsuranceState,
    private lclShipmentRepository: ShipmentRepository,
  ) {}

  public createCurrentEntryBill(coupon: string): Observable<BillModel[]> {
    const companyId: number = this.companiesService.getCompany() ? this.companiesService.getCompany().id : null;
    const referenceNumber: string = this.entrySummary$.getValue().referenceNumber;
    return this.billsRepository
      .create(companyId, { referenceNumbers: [referenceNumber], coupon, paymentProvider: null, paymentCodeId: null })
      .pipe(
        tap((bills) => {
          (bills || []).forEach((bill) => {
            this.ga.checkOutStep(bill, enumGACheckoutSteps.PaymentDelayed, bill.providerTransactionId);
          });
        }),
      );
  }

  public getPendingBills(isShowLoader: boolean = true, coupon?: string): Observable<BillItemPendingModel[]> {
    const { submissionType, id, state } = this.entrySummary$.getValue();
    if (!id || (submissionType === enumEntrySummarySubmissionType.MANUAL && state !== enumEntrySummaryState.DUTY_CALCULATION_COMPLETE)) {
      this.pendingBills$.next([]);
      this.canceledBills$.next([]);
      return of([]);
    }

    return this.getPendingAndCanceledBills(isShowLoader, coupon).pipe(map((data) => data.pending));
  }

  public getPendingAndCanceledBills(
    isShowLoader: boolean,
    coupon?: string,
  ): Observable<{ pending: BillItemPendingModel[]; canceled: BillItemPendingModel[] }> {
    return forkJoin({
      pending: this.billsRepository.pendingBills(
        this.companiesService.getCompany().id,
        { referenceNumbers: [this.entrySummary$.getValue().referenceNumber], paymentProvider: null, paymentCodeId: null, coupon },
        isShowLoader,
      ),
      canceled: this.billsRepository.canceledBills(
        this.companiesService.getCompany().id,
        { referenceNumbers: [this.entrySummary$.getValue().referenceNumber], paymentProvider: null, paymentCodeId: null },
        isShowLoader,
      ),
    }).pipe(
      tap((data) => {
        this.pendingBills$.next(data.pending);
        this.canceledBills$.next(data.canceled);
      }),
    );
  }

  public isShowDocuments(entrySummary: EntrySummaryModel): boolean {
    return (
      entrySummary.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED ||
      (entrySummary.submissionType === enumEntrySummarySubmissionType.MANUAL &&
        entrySummary.bondRequest &&
        entrySummary.bondRequest.state === enumBondRequestState.CREATED)
    );
  }

  public updateCurrentEntrySummary(data: Partial<EntrySummaryModel>) {
    this.updateNextEntrySummary((entrySummary) => ({ ...entrySummary, ...data }));
  }

  public clearCurrentEntrySummary() {
    this.entrySummary$.complete();
    this.entryDocuments$.complete();
    localStorage.removeItem('ES_DOCUMENTS');
    this.entrySummary$ = new BehaviorSubject({} as EntrySummaryModel);
    this.entryDocuments$ = new BehaviorSubject([]);
    // #12298: drop any in-flight OCR state so a stale "processing" never carries the blocking overlay into the next ES.
    this.currentEsDocOCRState$.next(null);
  }

  public getSummaryForeignPortOfLadingData(entrySummary: EntrySummaryModel): {
    hasSingleExportData: boolean;
    foreignPortOfLadingId?: number;
    exportDate?: string;
  } {
    const { shipments, hasSingleExportData } = entrySummary;
    if (shipments && shipments.length > 0) {
      const { foreignPortOfLadingId, exportDate } = shipments[0];
      return { hasSingleExportData, foreignPortOfLadingId, exportDate };
    }
    return { hasSingleExportData };
  }

  public removeShipmentFromES(shipmentId: number) {
    this.updateNextEntrySummary((entrySummary) => {
      entrySummary.shipments = entrySummary.shipments.filter((item) => item.id !== shipmentId);
      return entrySummary;
    });
  }

  public getBrokerMessages(isShowLoader: boolean = true, isUsed: boolean = false): Observable<BrokerMessageModel[]> {
    return this.brokerMessagesRepository.getMessages(this.entrySummary$.getValue().id, isShowLoader, isUsed).pipe(
      tap(() => {
        if (!this.entrySummary$.getValue().hasNewBrokerMessages) {
          return;
        }
        this.brokerMessagesRepository
          .markAsRead(this.entrySummary$.getValue().id, false, false)
          .subscribe(() => this.entrySummary$.next({ ...this.entrySummary$.getValue(), hasNewBrokerMessages: false }));
      }),
    );
  }

  public sentMessageToBroker(content: string): Observable<never> {
    return this.getOneEntrySummary().pipe(switchMap((es) => this.brokerMessagesRepository.createMessage(es.id, content)));
  }

  public getBond(): Observable<BondModel> {
    return this.entrySummary$.pipe(
      map((entrySummary) => (entrySummary ? (entrySummary.bondRequest ? null : entrySummary.bondInfo || null) : null)),
    );
  }

  public getBondRequest(): Observable<BondRequestModel> {
    return this.entrySummary$.pipe(
      map((entrySummary) =>
        entrySummary && entrySummary.bondRequest && entrySummary.bondRequest.state !== enumBondRequestState.COMPLETED
          ? entrySummary.bondRequest
          : null,
      ),
    );
  }

  public createEntrySummary(data?: Partial<EntrySummaryModel>): Observable<EntrySummaryModel> {
    const company = this.companiesService.getCompany();
    if (!company) {
      return EMPTY;
    }
    if (data?.arrivalDate) {
      data.arrivalDate = DateHelper.startOfTheDayByUTC(data.arrivalDate);
    }
    if (data?.exportDate) {
      data.exportDate = DateHelper.startOfTheDayByUTC(data.exportDate);
    }
    let esRefNumber: EntrySummaryModel['referenceNumber'];
    return this.getOneEntrySummary().pipe(
      switchMap((entrySummary) => this.entrySummaryRepository.create(company.id, { ...entrySummary, ...data } || {})),
      tap((createdES: EntrySummaryModel) => this.updateCurrentEntrySummary(createdES)),
      switchMap((createdES: EntrySummaryModel) => {
        esRefNumber = createdES.referenceNumber;
        if (company.defaultBondReferenceNumber) {
          return this.initBond(company.id, company.defaultBondReferenceNumber);
        }
        return of(null);
      }),
      switchMap(() => this.loadEntrySummary(esRefNumber)),
    );
  }

  public resetEntrySummary(esData: Partial<EntrySummaryModel>) {
    this.clearCurrentEntrySummary();

    const company = this.companiesService.getCompany();
    if (!company) {
      return EMPTY;
    }

    let esRefNumber: EntrySummaryModel['referenceNumber'];

    return this.getOneEntrySummary().pipe(
      switchMap((entrySummary) => this.entrySummaryRepository.update(esData.id, { ...entrySummary, ...esData })),
      switchMap((entrySummary) =>
        this.updateShipments(
          esData.shipments,
          {
            foreignPortOfLadingId: null,
            exportDate: null,
          },
          entrySummary,
        ),
      ),
      switchMap((entrySummary) => this.resetShipmentsEntities(esData.shipments, entrySummary)),
      tap((entrySummary) => this.updateCurrentEntrySummary(entrySummary)),
      switchMap((entrySummary) => {
        esRefNumber = entrySummary.referenceNumber;
        return this.updateEntrySummary(entrySummary);
      }),
      switchMap(() => this.loadEntrySummary(esRefNumber)),
    );
  }

  public loadEntrySummary(referenceNumber?: string): Observable<EntrySummaryModel> {
    if (!referenceNumber) {
      referenceNumber = this.entrySummary$.getValue().referenceNumber;
    }
    return this.entrySummaryRepository.getEntrySummary(referenceNumber);
  }

  public copyCompanyPoaToEntryIfNeeded(entrySummary: EntrySummaryModel): Observable<DocumentModel[]> {
    return this.findEntryPoaDocument().pipe(
      switchMap((entryPoaDocument: DocumentModel) => {
        if (
          (entrySummary.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED ||
            (entrySummary.submissionType === enumEntrySummarySubmissionType.MANUAL &&
              entrySummary.bondRequest &&
              entrySummary.bondRequest.state === enumBondRequestState.CREATED)) &&
          !entryPoaDocument
        ) {
          return this.copyCompanyPoaToEntry(entrySummary.id, enumDocumentType.POWER_OF_ATTORNEY);
        }
        return of(null);
      }),
    );
  }

  public copyEntryPoaToCompany(documentId: number, fileName: string, isTrucker: boolean) {
    return this.documentsService.copyPoaToCompany(documentId, fileName, isTrucker);
  }

  public copyCompanyPoaToEntry(entryId: number, docType: enumDocumentType = enumDocumentType.POWER_OF_ATTORNEY) {
    return this.documentsService.copyCompanyPoaToEntry(entryId, docType);
  }

  public copyEntryPunToCompany(documentId: number, fileName: string) {
    return this.documentsService.copyPunToCompany(documentId, fileName);
  }

  public copyCompanyPunToEntryIfNeeded(entrySummary: EntrySummaryModel) {
    return this.findEntryPunDocument().pipe(
      switchMap((entryPunDocument) => {
        if (
          (entrySummary.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED ||
            (entrySummary.submissionType === enumEntrySummarySubmissionType.MANUAL &&
              entrySummary.dutiesPaymentType === enumEntrySummaryDutiesPaymentType.ACH)) &&
          !entryPunDocument
        ) {
          return this.copyCompanyPunToEntry(entrySummary.id);
        }
        return of(null);
      }),
    );
  }

  public copyCompanyPunToEntry(entryId: number) {
    return this.documentsService.copyCompanyPunToEntry(entryId);
  }

  public updateDocuments(isShowLoader: boolean) {
    return this.getOneEntrySummary().pipe(
      switchMap((entrySummary) => this.documentsService.getEntryDocuments(entrySummary.id, isShowLoader)),
      catchError(() => []),
      tap((documents) => {
        this.entryDocuments$.next(documents);
      }),
    );
  }

  public uploadDocument(document: DocumentCreateModel, file: File, hasPendingBills: boolean, companyId: number = null) {
    return this.uploadDocumentClear(document, file, companyId).pipe(
      switchMap((docId) =>
        this.updateDocuments(false).pipe(
          map(() => {
            return docId;
          }),
        ),
      ),
      tap(() => {
        if (!hasPendingBills && this.entrySummary$.getValue().state === enumEntrySummaryState.BROKER_PROCESSING_FAILED) {
          this.afterUploadDocument();
        }
      }),
    );
  }

  public updateDocumentsState(hasPendingBills: boolean) {
    return this.updateDocuments(false).pipe(
      tap(() => {
        if (!hasPendingBills && this.entrySummary$.getValue().state === enumEntrySummaryState.BROKER_PROCESSING_FAILED) {
          this.afterUploadDocument();
        }
      }),
    );
  }

  public uploadDocumentClear(document: DocumentCreateModel, file: File, companyId: number = null): Observable<number> {
    return this.documentsService.uploadDocument(document, file, companyId);
  }

  public uploadPoaDocument(
    document: DocumentCreateModel,
    file: File,
    uploadToCompany: boolean,
    hasPendingBills: boolean,
    isTrucker: boolean = false,
    companyId?: number,
  ) {
    return this.documentsService.uploadDocument(document, file, companyId).pipe(
      tap((id: number) => {
        if (id && uploadToCompany) {
          this.copyEntryPoaToCompany(id, document.fileName, isTrucker).pipe(take(1)).subscribe();
        }
      }),
      switchMap(() => this.updateDocuments(false)),
      tap(() => {
        if (!hasPendingBills && this.entrySummary$.getValue().state === enumEntrySummaryState.BROKER_PROCESSING_FAILED) {
          this.afterUploadDocument();
        }
      }),
    );
  }

  public uploadPunDocument(document: DocumentCreateModel, file: File, uploadToCompany: boolean, hasPendingBills: boolean) {
    return this.documentsService.uploadDocument(document, file).pipe(
      tap((id: number) => {
        if (id && uploadToCompany) {
          this.copyEntryPunToCompany(id, document.fileName).pipe(take(1)).subscribe();
        }
      }),
      switchMap(() => this.updateDocuments(false)),
      tap(() => {
        if (!hasPendingBills && this.entrySummary$.getValue().state === enumEntrySummaryState.BROKER_PROCESSING_FAILED) {
          this.afterUploadDocument();
        }
      }),
    );
  }

  public removeDocument(documentId: number) {
    return this.documentsService.removeDocument(documentId).pipe(switchMap(() => this.updateDocuments(false)));
  }

  public updateDocumentState(documentId: number, state: enumDocumentState) {
    const documents = cloneDeep(this.entryDocuments$.getValue());
    this.entryDocuments$.next(documents.map((document) => (document.id === documentId ? { ...document, state } : document)));
  }

  public getDownloadLink(documentId: number): Observable<{ downloadUrl: string }> {
    return this.documentsService.getDownloadLink(documentId);
  }

  public removeInvoice(shipmentId: number, invoiceId: number): Observable<any> {
    return this.invoicesRepository.delete(invoiceId).pipe(
      take(1),
      switchMap(() => {
        return this.removeInvoiceFromCurrentES(invoiceId, shipmentId);
      }),
    );
  }

  public createInvoice(shipmentId: number, invoice: Partial<InvoiceModel>): Observable<InvoiceModel> {
    return this.invoicesRepository.create(shipmentId, invoice);
  }

  public removeShipment(shipmentId: number): void {
    this.shipmentsV2Repository
      .delete(shipmentId)
      .pipe(take(1))
      .subscribe(() => {
        this.removeShipmentFromES(shipmentId);
        this.shipmentRemoved$.next(shipmentId);
      });
  }

  public getDuties(esId: number): Observable<EntrySummaryDutiesModel> {
    return this.entrySummaryRepository.getDuties(esId);
  }

  public calculateDuties() {
    return this.selfEntryRepository.calculate(this.entrySummary$.getValue().id);
  }

  public sentCurrentSelfEntry() {
    return this.selfEntryRepository.sent(this.entrySummary$.getValue().id);
  }

  public sentCurrentBrokerEntry() {
    if (this.entrySummary$.getValue().state === enumEntrySummaryState.NOT_SENT) {
      return this.sentCurrentEntryToBroker();
    } else {
      return [enumEntrySummaryState.BROKER_PROCESSING_FINISHED].includes(this.entrySummary$.getValue().state)
        ? this.sentCurrentEntryToCbp()
        : of(null);
    }
  }

  public sentCurrentEntryToBroker() {
    return this.brokerEntryRepository.sentToBroker(this.entrySummary$.getValue().id);
  }

  public sentCurrentEntryToCbp() {
    return this.brokerEntryRepository.sentToCbp(this.entrySummary$.getValue().id);
  }

  public updateEntrySummary(data: Partial<EntrySummaryModel>) {
    if (data?.arrivalDate) {
      data.arrivalDate = DateHelper.startOfTheDayByUTC(data.arrivalDate);
    }
    if (data?.exportDate) {
      data.exportDate = DateHelper.startOfTheDayByUTC(data.exportDate);
    }
    return this.entrySummary$.pipe(
      take(1),
      switchMap((entrySummary) =>
        (data.submissionType === enumEntrySummarySubmissionType.MANUAL || data.type === enumEntrySummaryType.INFORMAL) &&
        entrySummary.bondRequest &&
        entrySummary.bondRequest.id
          ? this.bondRequestRepository.delete(entrySummary.bondRequest.id).pipe(map(() => entrySummary))
          : of(entrySummary),
      ),
      switchMap((entrySummary) => {
        const payload: Partial<EntrySummaryModel> = {
          ...entrySummary,
          ...data,
        };
        // fields hidden for the transportation mode must not linger in the data (#13045, #13261):
        // vessel exists only for Ocean; trip (flight/voyage/rail trip) has no Truck counterpart.
        if (payload.transportationMode !== enumTransportationMode.OCEAN) {
          payload.vesselName = null;
        }
        if (payload.transportationMode === enumTransportationMode.TRUCK) {
          payload.trip = null;
        }
        return this.entrySummaryRepository.update(entrySummary.id, payload);
      }),
    );
  }

  public updatePlatform(esId: number, platformType: enumPlatformType): Observable<EntrySummaryModel> {
    return this.entrySummaryRepository.updatePlatform(esId, platformType);
  }

  public createShipment(shipment: any, updatedEntrySummary?: EntrySummaryModel, isShowLoader?: boolean): Observable<ShipmentModel> {
    return this.entrySummary$.pipe(
      take(1),
      switchMap((entrySummary) => {
        return this.getDataForCreateNewShipment(shipment, updatedEntrySummary ? updatedEntrySummary : entrySummary);
      }),
      switchMap(({ esId, data }) => {
        if (data.exportDate) {
          data.exportDate = DateHelper.startOfTheDayByUTC(data.exportDate);
        }
        if (data.itDate) {
          data.itDate = DateHelper.startOfTheDayByUTC(data.itDate);
        }
        return this.shipmentsV2Repository.create(esId, data, isShowLoader);
      }),
    );
  }

  public updateShipment(shipment: Partial<ShipmentModel>, data: Partial<ShipmentModel>) {
    const newShipment: Partial<ShipmentModel> = { ...shipment, ...data };
    if (newShipment.exportDate) {
      newShipment.exportDate = DateHelper.startOfTheDayByUTC(newShipment.exportDate);
    }
    if (newShipment.itDate) {
      newShipment.itDate = DateHelper.startOfTheDayByUTC(newShipment.itDate);
    }
    return this.shipmentsV2Repository.update(shipment.id, newShipment);
  }

  public createEntity(data: Partial<EntityModel>, isOnlyUsa?: boolean): Observable<EntityModel> {
    if (!this.companiesService.getCompany() || (isOnlyUsa && data?.address?.country?.code !== USA_COUNTRY_CODE)) {
      return of(null);
    }

    return this.entitiesRepository.createEntity(this.companiesService.getCompany().id, data);
  }

  public updateEntity(id: number, data: Partial<EntityModel> = {}) {
    return this.entitiesRepository.updateEntity(id, data);
  }

  public updateBond(bond: BondModel): Observable<BondModel> {
    const entry = this.entrySummary$.getValue();
    return forkJoin({ updatedBond: this.entrySummaryRepository.updateBond(entry.id, bond), delete: this.deleteSingleBondRequest() }).pipe(
      map(({ updatedBond }) => updatedBond),
    );
  }

  public singleBondRequest(portCode: string): Observable<any> {
    const entry = this.entrySummary$.getValue();
    return this.createOrUpdateBondRequest({
      bondType: enumBondType.SINGLE_TRANSACTION,
      portCode,
      bondSubType: enumBondSubType.SINGLE_ENTRY,
      entrySummaryId: entry.id,
    }).pipe(
      switchMap((value) =>
        entry.bondRequest && entry.bondRequest.id === value.id
          ? of(null)
          : this.entrySummaryRepository.connectBondRequest(entry.id, value.id),
      ),
    );
  }

  public deleteSingleBondRequest(): Observable<any> {
    const entry = this.entrySummary$.getValue();
    if (entry.bondRequest && entry.bondRequest.id) {
      return this.entrySummaryRepository.disconnectBondRequest(entry.id).pipe(
        switchMap(() => {
          if (entry.bondRequest.state === enumBondRequestState.CREATED) {
            return this.bondRequestRepository.delete(entry.bondRequest.id);
          }
          return of(null);
        }),
      );
    }
    return of(null);
  }

  public searchPlaceOfDelivery(term: string) {
    term = term.trim();
    return this.selfFilerRepository.getSelfFiler(this.companiesService.getCompany().id, true).pipe(
      switchMap((cbpConnections: SelfFilerModel) => {
        if (cbpConnections.allowedPorts && cbpConnections.allowedPorts.length) {
          return of(
            cbpConnections.allowedPorts.filter(
              (port) => port.code.toLowerCase().includes(term.toLowerCase()) || port.description.toLowerCase().includes(term.toLowerCase()),
            ),
          );
        }

        return this.codeRepository.searchDomesticPorts(term);
      }),
    );
  }

  public createLineItem(item: Partial<LineModel>, invoiceId: number): Observable<LineModel> {
    return this.lineItemsRepository.create(invoiceId, item);
  }

  public updateLineItem(
    item: Partial<LineModel>,
    itemId: number,
    customErrorHandlerFunction?: (err: HttpErrorResponse) => Observable<never>,
  ): Observable<LineModel> {
    return this.lineItemsRepository.update(itemId, item, customErrorHandlerFunction);
  }

  public deleteLineItem(itemId: number): Observable<never> {
    return this.lineItemsRepository.delete(itemId);
  }

  public bulkDeleteLineItems(itemIds: number[]): Observable<never> {
    return this.lineItemsRepository.bulkDelete(itemIds);
  }

  public esByShipments(shipmentIsfIdList: number[]) {
    return this.entrySummary$.pipe(
      take(1),
      switchMap((entrySummary) => {
        const arr = shipmentIsfIdList.map((shipmentIsfId) =>
          this.shipmentsV2Repository.fillEntrySummaryByIsfShipment(entrySummary.id, shipmentIsfId),
        );
        return forkJoin(arr);
      }),
    );
  }

  public changeTransportationMode(transportationMode: enumTransportationMode): Observable<EntrySummaryModel> {
    let label = null;

    switch (transportationMode) {
      case enumTransportationMode.AIR:
        label = 'Air';
        break;
      case enumTransportationMode.OCEAN:
        label = 'Ocean';
        break;
      case enumTransportationMode.RAIL:
        label = 'Rail';
        break;
      case enumTransportationMode.TRUCK:
        label = 'Truck';
        break;
      default:
        break;
    }

    return this.entrySummary$.pipe(
      take(1),
      tap((es) => {
        this.ga.emitCustomEvent(enumGATagEventCategory.ES, enumGATagEventAction.ESFreightType, `${es.referenceNumber} ${label}`);
      }),
      switchMap((entrySummary) => {
        if (entrySummary) {
          entrySummary.transportationMode = transportationMode;
        } else {
          entrySummary = { transportationMode: transportationMode } as EntrySummaryModel;
        }
        return this.updateEntrySummary(entrySummary);
      }),
      tap((entrySummary) => {
        this.entrySummary$.next(entrySummary);
      }),
    );
  }

  public removeItemFromCurrentES(itemId: number, invoiceId: number, shipmentId: number) {
    this.entrySummary$.pipe(take(1)).subscribe((entrySummary) => {
      const shipmentIndex = entrySummary.shipments.findIndex((item) => item.id === shipmentId);
      if (shipmentIndex === -1) {
        return;
      }
      const invoiceIndex = entrySummary.shipments[shipmentIndex].invoices.findIndex((item) => item.id === invoiceId);
      if (invoiceIndex === -1) {
        return;
      }
      const itemIndex = entrySummary.shipments[shipmentIndex].invoices[invoiceIndex].lines.findIndex((item) => item.id === itemId);
      if (itemIndex === -1) {
        return;
      }
      entrySummary.shipments[shipmentIndex].invoices[invoiceIndex].lines = entrySummary.shipments[shipmentIndex].invoices[
        invoiceIndex
      ].lines.filter((item) => item.id !== itemId);
      this.entrySummary$.next(entrySummary);
    });
  }

  public removeInvoiceFromCurrentES(invoiceId: number, shipmentId: number) {
    return this.entrySummary$.pipe(
      take(1),
      tap((entrySummary) => {
        const shipmentIndex = entrySummary.shipments.findIndex((item) => item.id === shipmentId);
        if (shipmentIndex === -1) {
          return;
        }
        const invoiceIndex = entrySummary.shipments[shipmentIndex].invoices.findIndex((item) => item.id === invoiceId);
        if (invoiceIndex === -1) {
          return;
        }
        entrySummary.shipments[shipmentIndex].invoices = entrySummary.shipments[shipmentIndex].invoices.filter(
          (item) => item.id !== invoiceId,
        );
        this.entrySummary$.next(entrySummary);
      }),
    );
  }

  public itMinDate(entrySummary: EntrySummaryModel, exportDate?: string): string {
    if (!entrySummary) {
      return null;
    }

    if (!exportDate) {
      exportDate = this.getSummaryForeignPortOfLadingData(entrySummary).exportDate;
    }

    return exportDate;
    // DateHelper.max([exportDate, entrySummary.arrivalDate]);
  }

  public itMaxDate(entrySummary: EntrySummaryModel): string {
    if (!entrySummary) {
      return null;
    }
    return entrySummary.arrivalDate;
  }

  public arrivalMinDate(
    entrySummary: EntrySummaryModel,
    exportDate: string,
    hasSingleExportData: boolean,
    submissionType: enumEntrySummarySubmissionType,
  ): string {
    if (!entrySummary || submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED) {
      return null;
    }
    exportDate = !hasSingleExportData ? exportDate : this.getMaxExportDateFromShipments(entrySummary.shipments) || null;
    let minDate: string = DateHelper.startOfTheDayByUTC(DateHelper.now);
    minDate = DateHelper.addDaysByUTC(minDate, -7);
    if (!exportDate) {
      return minDate;
    }
    return DateHelper.max([exportDate, minDate]);
  }

  public getESValidationErrorData(entrySummary: EntrySummaryModel, attachedDocuments: DocumentModel[]): Observable<IEsValidationData> {
    return this.entrySummaryValidationService.getEntryValidationErrorData(entrySummary, attachedDocuments);
  }

  public getImporterFromCompany(company: any): EntityModel {
    const { address, addressId, dateOfBirth, identifier, identifierQualifier } = company;
    const name: string = company.businessName || `${company.firstName} ${company.lastName}`;
    const user: UserModel = this.companiesService.getUser();

    return new EntityModel({
      id: 0,
      address: { ...address, id: 0 },
      addressId,
      dateOfBirth: dateOfBirth,
      name,
      entityIdentifier: identifier,
      entityIdentifierQualifier: identifierQualifier,
      entityCode: enumEntityModelCode.IMPORTER,
      phone: user?.phoneNumber,
      email: user?.email,
    });
  }

  public getEntrySummaryEnteredValue(entrySummary: EntrySummaryModel): Observable<number> {
    return this.entrySummaryService.getEntrySummaryEnteredValue(entrySummary);
  }

  public getImporterForShipmentFromCompany(): Observable<EntityModel> {
    return this.companiesService.getCompanyAsync().pipe(
      switchMap((company) => {
        const entity = this.getImporterFromCompany(company);
        return this.createEntity(entity);
      }),
    );
  }

  public isESSynced(state: enumEntrySummaryState): boolean {
    // #5820
    // check if ES is synced with LDS
    return state >= enumEntrySummaryState.BROKER_PROCESSING_FINISHED;
  }

  public isCreatingInvoiceStepInvalid(entrySummary: EntrySummaryModel): boolean {
    if (!entrySummary) {
      true;
    }

    const shipments = entrySummary.shipments;
    const validitySteps: boolean[] = [];

    shipments.forEach((shipment) => {
      const invoices = shipment.invoices;
      const isQuickFixShipmentMainDetailsInvalid = this.entrySummaryValidationService.isQuickFixShipmentMainDetailsInvalid(
        entrySummary,
        shipment,
      );
      const isQuickFixBlDetailsInvalid = this.entrySummaryValidationService.isQuickFixBlDetailsInvalid(entrySummary, shipment);
      const isQuickFixSellerAndConsigneeDetailsInvalid =
        this.entrySummaryValidationService.isQuickFixSellerAndConsigneeDetailsInvalid(shipment);
      const sellerAndConsigneeDetailsErrors = [
        ...(shipment.seller?.validationMessages || []),
        ...(shipment.consignee?.validationMessages || []),
      ];
      const isQuickFixTosDetailsInvalid = this.entrySummaryValidationService.isQuickFixTosDetailsInvalid(shipment);

      validitySteps.push(isQuickFixShipmentMainDetailsInvalid);
      validitySteps.push(isQuickFixBlDetailsInvalid);
      validitySteps.push(!!sellerAndConsigneeDetailsErrors?.length || isQuickFixSellerAndConsigneeDetailsInvalid);
      validitySteps.push(isQuickFixTosDetailsInvalid);

      invoices.forEach((invoice) => {
        const invoiceErrorsToSkip = [
          INVOICE_OCR_STATE_NOT_REVIEWED,
          INVOICE_MANUFACTURER_MISSING,
          LINES_MISSING,
          MANUFACTURER_EXPECTED_TO_BE_CANADIAN,
        ];
        const invoiceMainDetailsErrors = (invoice?.validationMessages || []).filter((error) => {
          return !invoiceErrorsToSkip.includes(error.text);
        });
        const isQuickFixInvoiceDetailsInvalid = this.entrySummaryValidationService.isQuickFixInvoiceDetailsInvalid(invoice);
        const manufacturerDetailsErrors = invoice?.manufacturer?.validationMessages || [];
        const manufacturerAddressErrors = invoice?.manufacturer?.address?.validationMessages || {};
        const isQuickFixManufacturerDetailsInvalid = this.entrySummaryValidationService.isQuickFixManufacturerDetailsInvalid(invoice);
        const isManufacturerExpectedFromCanada =
          (invoice?.validationMessages || []).some((e) => e.text === MANUFACTURER_EXPECTED_TO_BE_CANADIAN) ||
          (invoice?.manufacturer?.validationMessages || []).some((e) => e.text === MANUFACTURER_EXPECTED_TO_BE_CANADIAN);

        validitySteps.push(!!invoiceMainDetailsErrors?.length || isQuickFixInvoiceDetailsInvalid);
        validitySteps.push(
          !!manufacturerDetailsErrors?.length ||
            !UtilsHelper.isObjectEmpty(manufacturerAddressErrors) ||
            isQuickFixManufacturerDetailsInvalid ||
            isManufacturerExpectedFromCanada,
        );
      });
    });

    return validitySteps.includes(true);
  }

  public createEmptyES(
    esData: LclEntrySummaryUpsertModel,
    freightCost: number,
    shipmentId: number,
    lclData: LclIsfEsLinkModel,
    invoiceDocument?: DocumentModel,
  ): Observable<EntrySummaryModel> {
    const company: CompanyModel = this.companiesService.getCompany();
    const achMethodAllowed: boolean = company?.authorizationAgreementAccepted && !!company?.payerUnitNumber;

    return this.createEntity({ ...esData.consignee, entityCode: enumEntityModelCode.IMPORTER, id: null }, true).pipe(
      switchMap((importerEntity: EntityModel) =>
        this.entrySummaryRepository.create(company.id, {
          type: enumEntrySummaryType.FORMAL,
          hasSingleExportData: false,
          transportationMode: enumTransportationMode.OCEAN,
          submissionType: enumEntrySummarySubmissionType.BROKER_ASSISTED,
          dutiesPaymentType: achMethodAllowed ? enumEntrySummaryDutiesPaymentType.ACH : enumEntrySummaryDutiesPaymentType.INSTANT,
          payerUnitNumber: null,
          importerId: importerEntity?.id || null,
          vesselName: esData.voyage?.vesselName,
          trip: esData.voyage?.voyageNumber,
          arrivalDate: !!esData.voyage?.actualArrivalTime ? esData.voyage?.actualArrivalTime : esData.voyage?.estimatedArrivalTime,
          portOfEntryCode: esData.voyage?.route?.arrivalPort?.code,
          portOfUnladingCode: esData.voyage?.route?.arrivalPort?.code,
        }),
      ),
      mergeMap((es: EntrySummaryModel) =>
        combineLatest([of(es), this.lclShipmentRepository.setESData(shipmentId, { ...lclData, referenceNumber: es.referenceNumber })]),
      ),
      mergeMap(([es, _]: [EntrySummaryModel, LCLShipmentModel]) =>
        combineLatest([of(es), this.createEntity({ ...esData.shipper, entityCode: enumEntityModelCode.SELLER, id: null })]),
      ),
      mergeMap(([es, seller]: [EntrySummaryModel, EntityModel]) =>
        combineLatest([of(es), of(seller), this.codeRepository.searchForeignPort(esData.voyage?.route?.departurePort?.code)]),
      ),
      mergeMap(([es, seller, ports]: [EntrySummaryModel, EntityModel, ForeignPortModel[]]) =>
        combineLatest([
          of(es),
          of(seller),
          of(ports),
          this.codeRepository.searchShipmentUnits(
            esData.packages[0]?.type === enumPackageType.BOX
              ? enumPackageType[enumPackageType.PACKAGE]
              : enumPackageType[esData.packages[0]?.type],
          ),
        ]),
      ),
      mergeMap(([es, seller, ports, quantityUnits]: [EntrySummaryModel, EntityModel, ForeignPortModel[], UnitModel[]]) =>
        combineLatest([
          of(es),
          this.shipmentsV2Repository.create(es.id, {
            sellerId: seller?.id || null,
            consigneeId: es.importerId || null,
            exportDate: !!esData.voyage?.actualDepartureTime ? esData.voyage?.actualDepartureTime : esData.voyage?.estimatedDepartureTime,
            foreignPortOfLadingId: ports.find((portItem: ForeignPortModel) => portItem.code === esData.voyage?.route?.departurePort?.code)
              ?.id,
            masterBill: {
              carrier: esData.container?.billNumber?.carrier,
              number: esData.container?.billNumber?.number,
            },
            houseBill: {
              carrier: esData.houseBill?.carrier,
              number: esData.houseBill?.number,
            },
            quantity: esData.packages.reduce((acc: number, curr: PackageModel) => acc + curr.quantity, 0),
            freightCost: freightCost,
            chargesDistributionType: [enumIncotermsType.FCA, enumIncotermsType.FOB].includes(esData.incoTermsType)
              ? enumChargesDistributionType.COLLECT
              : enumChargesDistributionType.PREPAID,
            quantityUnit: quantityUnits[0]?.code,
          }),
        ]),
      ),
      mergeMap(([es, shipment]: [EntrySummaryModel, ShipmentModel]) =>
        combineLatest([
          of({ ...es, shipments: [shipment] }),
          this.invoicesRepository.create(shipment.id, {
            manufacturerId: shipment?.sellerId || null,
            incoTermsCode: enumIncotermsType[esData.incoTermsType],
          }),
        ]),
      ),
      mergeMap(([es, invoice]: [EntrySummaryModel, InvoiceModel]) => {
        es.shipments[0].invoices = [invoice];
        return combineLatest([of(es), this.createLinesForInvoice(invoice.id, esData)]);
      }),
      switchMap(([es]: [EntrySummaryModel, LineModel[]]) => this.createDocsForLCL(es, invoiceDocument).pipe(switchMap(() => of(es)))),
    );
  }

  public uploadDocStripDocument(data: { document: DocumentModel; shipmentId: number; isForceAdd?: boolean; isInvoiceReplace?: boolean }) {
    this.uploadDocsStripDocument$.next(data);
  }

  public setConnectedIsf(entrySummaryId: string, isfId: string): Observable<ShortModel> {
    return this.entrySummaryRepository.setConnectedIsf(entrySummaryId, isfId);
  }

  public getEsState(id: number): Observable<enumEntrySummaryState> {
    return this.entrySummaryRepository.getState(id);
  }

  public getInsuranceAvailability(entry: EntrySummaryModel): Observable<{ insurance: MarineInsuranceModel; isAvailable: boolean }> {
    return this.marineInsuranceAvailabilityService.isShowCreateForEntrySummary(entry);
  }

  public getInsuranceDocuments(id: number): Observable<DocumentModel> {
    return this.marineInsuranceFacade.getDocuments(id);
  }

  public getInsurancePendingBills(companyId: number, referenceNumber: string): Observable<PaymentBillItemPendingModel> {
    return this.marineInsuranceFacade.getPendingBills(companyId, [referenceNumber], null, null, false);
  }

  public updateInsuranceShipmentDate(entrySummary: EntrySummaryModel): Observable<[MarineInsuranceModel, PaymentBillItemPendingModel]> {
    const lastEntryExportDate: string = entrySummary?.shipments[0]?.exportDate;
    const currentMarineInsurance: MarineInsuranceModel = this.marineInsuranceState.currentInsurance.getValue();

    if (entrySummary.shipments?.length > 1 || entrySummary.transportationMode === enumTransportationMode.RAIL) {
      return of([null, null]);
    } else {
      if (!currentMarineInsurance) {
        return of([null, null]);
      }

      return this.marineInsuranceFacade
        .updateQuote({ shippingDate: lastEntryExportDate }, currentMarineInsurance, entrySummary.companyId)
        .pipe(
          tap(([insurance, pendingBills]: [MarineInsuranceModel, PaymentBillItemPendingModel]) => {
            this.marineInsuranceState.saveData(insurance, true, false);
            this.marineInsuranceState.saveBillsData(pendingBills);
          }),
        );
    }
  }

  public isFilesValid(es: EntrySummaryShortModel | EntrySummaryModel, documents: DocumentModel[]): boolean {
    if (!es || !documents?.length) {
      return;
    }

    const docs: DocumentModel[] = (documents || []).filter(
      (item: DocumentModel) =>
        item.rootEntityId === es.id && [enumDocumentState.UPLOADED, enumDocumentState.SCAN_PENDING].includes(item.state),
    );
    const isHasPOA: boolean = !!docs.find(
      (item: DocumentModel) => item.type === enumDocumentType.POWER_OF_ATTORNEY || item.type === enumDocumentType.POWER_OF_ATTORNEY_TRUCKER,
    );
    const isHasPUN: boolean = !!docs.find((item: DocumentModel) => item.type === enumDocumentType.ACH_APPROVAL);
    const isHasBLAndInvoiceDocs: boolean = !!(es.shipments || []).every(
      (shipmentItem: ShipmentModel) =>
        docs.find((docItem: DocumentModel) =>
          es.transportationMode === enumTransportationMode.TRUCK
            ? true
            : docItem.relatedEntityId === shipmentItem.id &&
              docItem.type === enumDocumentType.BILL_OF_LADING &&
              docItem.relatedEntityType === enumDocumentRelatedEntityType.SHIPMENT,
        ) &&
        shipmentItem.invoices.every((invoiceItem: InvoiceModel) =>
          docs.find(
            (docItem: DocumentModel) =>
              docItem.relatedEntityId === invoiceItem.id &&
              docItem.type === enumDocumentType.INVOICE &&
              docItem.relatedEntityType === enumDocumentRelatedEntityType.INVOICE,
          ),
        ),
    );

    return !!isHasPOA && !!(this.companiesService?.isHasActiveACH ? isHasPUN : true) && !!isHasBLAndInvoiceDocs;
  }

  public runDocumentRecognition(invoiceId: number, runDocumentRecognition: boolean, isShowLoader: boolean = false): Observable<never> {
    return this.invoicesRepository.runDocumentRecognition(invoiceId, runDocumentRecognition, isShowLoader);
  }

  public getEsOcrStatus(entrySummaryId: number, isShowLoader: boolean = false): Observable<enumOcrStatus> {
    return this.entrySummaryRepository.getOcrStatus(entrySummaryId, isShowLoader);
  }

  public getEsDocOcrStatus(entrySummaryId: number, documentId: number, isShowLoader: boolean = false): Observable<enumOcrStatus> {
    return this.documentsRepository.getESDocuments(entrySummaryId, isShowLoader).pipe(
      map((docs) => {
        return docs.find((doc) => doc.id === documentId).ocrStatus;
      }),
    );
  }

  public getOcrStatus(invoiceId: number, isShowLoader: boolean = false): Observable<enumOcrStatus> {
    return this.invoicesRepository.getOcrStatus(invoiceId, isShowLoader);
  }

  public setOcrStatus(invoiceId: number, isShowLoader: boolean = false): Observable<never> {
    return this.invoicesRepository.setOcrStatus(invoiceId, isShowLoader);
  }

  public cleanInvoice(invoiceId: number, invoiceData: Partial<InvoiceModel>): Observable<InvoiceModel> {
    return this.invoicesRepository.update(invoiceId, invoiceData).pipe(
      switchMap(() => this.invoicesRepository.get(invoiceId)),
      switchMap((invoice: InvoiceModel) => {
        if (invoiceData.manufacturer?.entityCode === enumEntityModelCode.MANUFACTURER && invoice.manufacturerId) {
          return this.entitiesRepository.deleteEntity(invoiceData.manufacturerId).pipe(map(() => invoice));
        } else {
          return of(invoice);
        }
      }),
      switchMap((invoice: InvoiceModel) => {
        return invoice?.lines?.length
          ? forkJoin(invoice.lines.map((line: LineModel) => this.deleteLineItem(line.id))).pipe(map(() => invoice))
          : of(invoice);
      }),
    );
  }

  public getThumbLink(entityId: number, docType: enumDocumentType, fallbackDocType: enumDocumentType): Observable<{ downloadUrl: string }> {
    return this.entryDocuments$.pipe(
      switchMap((docs: DocumentModel[]) => {
        const formattedPreviewId: number = docs.find((doc: DocumentModel) => doc.type === docType && doc.relatedEntityId === entityId)?.id;
        const defaultPreviewId: number = docs.find(
          (doc: DocumentModel) => doc.type === fallbackDocType && doc.relatedEntityId === entityId,
        )?.id;

        return formattedPreviewId || defaultPreviewId
          ? this.getDownloadLink(formattedPreviewId ? formattedPreviewId : defaultPreviewId)
          : of(null);
      }),
    );
  }

  public processEsOCR(entrySummaryId: number, isClearStatusOnFinish: boolean = false): Observable<enumOcrStatus> {
    let retriesCounter: number = 0;
    let currentStatus: enumOcrStatus;

    return this.entrySummaryRepository.ocrProcessing(entrySummaryId).pipe(
      delay(DEFAULT_LONGER_POLLING_TIMEOUT), //timeout to allow user enough time to see status of ui change
      tap(() => this.currentOCRState$.next(enumOcrStatus.PROCESSING)),
      switchMap(() => {
        return timer(0, DEFAULT_LONGER_POLLING_TIMEOUT).pipe(
          takeWhile(() => {
            const exitingStatuses = [
              enumOcrStatus.CANCELED,
              enumOcrStatus.EXTRACTION_FAILED,
              enumOcrStatus.PARTIALLY_PROCESSED,
              enumOcrStatus.PROCESSED,
            ];
            return retriesCounter < DEFAULT_MAX_POLLING_RETRY && !exitingStatuses.includes(currentStatus);
          }),
          switchMap(() =>
            this.getEsOcrStatus(entrySummaryId).pipe(
              catchError((err) => {
                return of(enumOcrStatus.PROCESSING);
              }),
            ),
          ),
          catchError((err) => {
            return of(enumOcrStatus.PROCESSING);
          }),
          tap((status: enumOcrStatus) => {
            currentStatus = status;
            if (status > enumOcrStatus.PROCESSING) {
              this.currentOCRState$.next(status);
            }
          }),
          switchMap((status: enumOcrStatus) => {
            retriesCounter++;
            if (retriesCounter < DEFAULT_MAX_POLLING_RETRY && status !== enumOcrStatus.EXTRACTION_FAILED) {
              return of(enumOcrStatus.PROCESSING);
            } else {
              return of(status);
            }
          }),
          delay(DEFAULT_LONGER_POLLING_TIMEOUT), //timeout to allow user enough time to see status of ui change
          filter((status: enumOcrStatus) => {
            const statusesToSkip = [
              enumOcrStatus.CONVERTING_FILE_TO_IMAGE,
              enumOcrStatus.EXTRACTING_DATA,
              enumOcrStatus.CLASSIFYING_LINE_ITEMS,
            ];

            if (retriesCounter < DEFAULT_MAX_POLLING_RETRY) {
              statusesToSkip.push(enumOcrStatus.PROCESSING);
            }

            return !statusesToSkip.includes(status);
          }),
          first(),
          catchError((err) => of(currentStatus)),
        );
      }),
      tap(() => {
        if (isClearStatusOnFinish) {
          this.currentOCRState$.next(null);
        }
      }),
    );
  }

  public processEsDocOCR(
    entrySummaryId: number,
    data: { file: File; docId: number; docHash: string },
    isClearStatusOnFinish: boolean = false,
  ): Observable<enumOcrStatus> {
    const { docId } = data;

    let retriesCounter: number = 0;
    let currentStatus: enumOcrStatus;
    const exitingStatuses = [
      enumOcrStatus.CANCELED,
      enumOcrStatus.EXTRACTION_FAILED,
      enumOcrStatus.PARTIALLY_PROCESSED,
      enumOcrStatus.PROCESSED,
    ];

    return this.documentsRepository.ocrProcessing(docId).pipe(
      delay(DEFAULT_LONGER_POLLING_TIMEOUT), //timeout to allow user enough time to see status of ui change
      tap(() => this.currentEsDocOCRState$.next(enumOcrStatus.PROCESSING)),
      switchMap(() => this.uploadFile(data, true)),
      switchMap(() => {
        return timer(0, DEFAULT_LONGER_POLLING_TIMEOUT).pipe(
          takeWhile(() => {
            return retriesCounter < DEFAULT_MAX_POLLING_RETRY && !exitingStatuses.includes(currentStatus);
          }),
          switchMap(() =>
            this.getEsDocOcrStatus(entrySummaryId, docId, false).pipe(
              catchError((err) => {
                return of(enumOcrStatus.PROCESSING);
              }),
            ),
          ),
          catchError((err) => {
            return of(enumOcrStatus.PROCESSING);
          }),
          tap((status: enumOcrStatus) => {
            currentStatus = status;
            if (status > enumOcrStatus.PROCESSING) {
              this.currentEsDocOCRState$.next(status);
            }
          }),
          switchMap((status: enumOcrStatus) => {
            retriesCounter++;
            if (retriesCounter < DEFAULT_MAX_POLLING_RETRY && !exitingStatuses.includes(currentStatus)) {
              return of(enumOcrStatus.PROCESSING);
            } else {
              return of(status);
            }
          }),
          delay(DEFAULT_LONGER_POLLING_TIMEOUT), //timeout to allow user enough time to see status of ui change
          filter((status: enumOcrStatus) => {
            const statusesToSkip = [
              enumOcrStatus.CONVERTING_FILE_TO_IMAGE,
              enumOcrStatus.EXTRACTING_DATA,
              enumOcrStatus.CLASSIFYING_LINE_ITEMS,
            ];

            if (retriesCounter < DEFAULT_MAX_POLLING_RETRY) {
              statusesToSkip.push(enumOcrStatus.PROCESSING);
            }

            return !statusesToSkip.includes(status);
          }),
          first(),
          catchError((err) => of(currentStatus)),
        );
      }),
      tap(() => {
        if (isClearStatusOnFinish) {
          this.currentEsDocOCRState$.next(null);
        }
      }),
    );
  }

  public processInvoiceOCR(
    existingInvoice: InvoiceModel,
    billOfLadingId: number,
    invoiceId: number,
    rootEntityId: number,
    rootEntityType: enumDocumentRootEntityType,
    company: CompanyModel,
    file: File,
    pollingTimeout: number,
    maxPollingRetry: number,
    isRunOCR: boolean,
  ): Observable<{ status: enumOcrStatus; invoiceId: number }> {
    this.currentOCRState$.next(enumOcrStatus.PROCESSING);

    return (!!existingInvoice?.id ? of(existingInvoice) : this.createInvoice(billOfLadingId, {})).pipe(
      switchMap((invoice: InvoiceModel) => {
        return invoiceId ? this.removeInvoice(billOfLadingId, invoiceId).pipe(map(() => invoice)) : of(invoice);
      }),
      tap((invoice: InvoiceModel) => (invoiceId = invoice.id)),
      switchMap(() => this.runDocumentRecognition(invoiceId, isRunOCR)),
      switchMap(() =>
        this.documentsService.uploadDocument(
          {
            rootEntityId: rootEntityId,
            rootEntityType: rootEntityType,
            relatedEntityId: invoiceId,
            relatedEntityType: enumDocumentRelatedEntityType.INVOICE,
            fileName: file.name,
            type: enumDocumentType.INVOICE,
            companyId: company.id,
          },
          file,
          company.id,
        ),
      ),
      switchMap((uploadedDocumentId: number) => {
        return timer(0, pollingTimeout).pipe(
          take(maxPollingRetry),
          switchMap(() =>
            uploadedDocumentId ? this.documentsService.getDocumentState(uploadedDocumentId, false) : of(enumDocumentState.SCAN_FAILED),
          ),
          filter((status: enumDocumentState) => ![enumDocumentState.CREATED, enumDocumentState.SCAN_PENDING].includes(status)),
          first(),
        );
      }),
      switchMap(() => {
        return timer(0, pollingTimeout).pipe(
          take(maxPollingRetry),
          switchMap(() => this.getOcrStatus(invoiceId)),
          tap((status: enumOcrStatus) => {
            if (status > enumOcrStatus.PROCESSING) {
              this.currentOCRState$.next(status);
            }
          }),
          filter(
            (status: enumOcrStatus) =>
              ![
                enumOcrStatus.PROCESSING,
                enumOcrStatus.CONVERTING_FILE_TO_IMAGE,
                enumOcrStatus.EXTRACTING_DATA,
                enumOcrStatus.CLASSIFYING_LINE_ITEMS,
              ].includes(status),
          ),
          first(),
        );
      }),
      switchMap((status: enumOcrStatus) => {
        return this.updateDocuments(true).pipe(map(() => ({ status, invoiceId })));
      }),
      tap(() => this.currentOCRState$.next(null)),
    );
  }

  private resetShipmentsEntities(shipmentsToUpdate: ShipmentModel[], entrySummary: EntrySummaryModel): Observable<EntrySummaryModel> {
    const invoices: InvoiceModel[] = [];
    shipmentsToUpdate.forEach((shipment) => {
      invoices.push(...(shipment.invoices || []));
    });

    return forkJoin(
      shipmentsToUpdate.map((shipment) => {
        return (!!shipment.sellerId ? this.updateEntity(shipment.sellerId, shipment.seller) : of(null)).pipe(
          switchMap((_) => (!!shipment.consigneeId ? this.updateEntity(shipment.consigneeId, shipment.consignee) : of(null))),
        );
      }),
    ).pipe(
      switchMap((_) => {
        if (!invoices?.length) {
          return of(null);
        }

        return forkJoin(
          invoices.map((invoice) => {
            return this.cleanInvoice(invoice.id, invoice);
          }),
        );
      }),
      switchMap((_) => of(entrySummary)),
    );
  }

  private updateShipments(
    shipmentsToUpdate: ShipmentModel[],
    fieldsToUpdate: Partial<ShipmentModel>,
    entrySummary: EntrySummaryModel,
  ): Observable<EntrySummaryModel> {
    return forkJoin(
      shipmentsToUpdate.map((shipment) => {
        return this.updateShipment(shipment, fieldsToUpdate);
      }),
    ).pipe(
      switchMap((updatedShipments: ShipmentModel[]) => {
        entrySummary.shipments = this.getUpdatedShipmentsForEntrySummary(updatedShipments, entrySummary);
        return of(entrySummary);
      }),
    );
  }

  private getUpdatedShipmentsForEntrySummary(updatedShipments: ShipmentModel[], entrySummary: EntrySummaryModel) {
    const updatedShipmentsId = updatedShipments.filter((item) => item).map((item) => item.id);
    return entrySummary.shipments.map((item) => {
      if (updatedShipmentsId.includes(item.id)) {
        return updatedShipments.find((updatedShipment) => item.id === updatedShipment.id);
      }
      return item;
    });
  }

  private afterUploadDocument() {
    const entry = this.entrySummary$.getValue();
    this.entrySummaryRepository
      .getState(entry.id)
      .pipe(take(1))
      .subscribe((state) => {
        if (state !== enumEntrySummaryState.BROKER_PROCESSING_PENDING) {
          this.afterUploadDocument();
        } else {
          this.updateNextEntrySummary(() => ({
            state: state,
          }));
        }
      });
  }

  private uploadFile(data: { file: File; docId: number; docHash: string }, isShowLoader: boolean): Observable<number> {
    const { file, docId, docHash } = data;

    return this.documentsRepository.uploadFile(docId, file, isShowLoader).pipe(
      map(() => docId),
      tap(() => this.documentsService.isUploadingDocIdsToUnshift$.next(docHash)),
      catchError(() => {
        return this.documentsRepository.delete(docId, isShowLoader);
      }),
    );
  }

  private initBond(companyId: CompanyModel['id'], bondReferenceNumber: CompanyBondModel['referenceNumber']) {
    return this.companyBondsRepository.get(companyId).pipe(
      map((bonds: CompanyBondModel[]) => bonds.find((bond) => bond.referenceNumber === bondReferenceNumber)),
      switchMap((defBond: CompanyBondModel) => {
        if (!defBond) {
          return of(null);
        }
        const bond: BondModel = new BondModel({
          type: defBond.type,
          referenceNumber: defBond.referenceNumber,
          suretyCode: defBond.suretyCode,
          bondAmount: defBond.amount,
        });
        return this.updateBond(bond);
      }),
    );
  }

  private getMaxExportDateFromShipments(shipments: ShipmentModel[] = []): string {
    let result: string;
    for (const shipment of shipments) {
      const exportDate = shipment.exportDate;
      if ((!result && exportDate) || (result && exportDate && DateHelper.isAfter(exportDate, result))) {
        result = exportDate;
      }
    }
    return result;
  }

  private getOneEntrySummary() {
    return this.entrySummary$.pipe(take(1));
  }

  private updateNextEntrySummary(updateFunc: (entrySummary: EntrySummaryModel) => Partial<EntrySummaryModel>) {
    this.getOneEntrySummary()
      .pipe(take(1))
      .subscribe((entrySummary) => {
        const result = { ...entrySummary, ...updateFunc(entrySummary) };
        this.entrySummary$.next(result);
      });
  }

  private getDataForCreateNewShipment(
    shipment: Partial<ShipmentModel>,
    entrySummary: EntrySummaryModel,
  ): Observable<{ esId: number; data: Partial<ShipmentModel> }> {
    const importerId =
      !!entrySummary?.importer?.id && entrySummary?.importer.entityIdentifierQualifier !== enumIdentifierQualifier.ANI
        ? entrySummary?.importer?.id
        : null;

    return this.setSellerIdForNewShipment(shipment, entrySummary).pipe(
      map((sellerId) => ({
        esId: entrySummary.id,
        data: {
          ...shipment,
          consigneeId: importerId,
          buyerId: importerId,
          sellerId,
        },
      })),
    );
  }

  private setSellerIdForNewShipment(shipment: Partial<ShipmentModel>, entrySummary: EntrySummaryModel): Observable<number> {
    const sellerId = shipment.sellerId ? shipment.sellerId : null;

    if (sellerId) {
      return of(sellerId);
    }

    if (entrySummary.shipments && entrySummary.shipments.length && entrySummary.shipments[0].sellerId) {
      return this.createEntity(entrySummary.shipments[0].seller).pipe(map((entity) => entity.id));
    }

    return of(null);
  }

  private findEntryPoaDocument(): Observable<DocumentModel> {
    return this.entryDocuments$.pipe(
      take(1),
      map((docs: DocumentModel[]) => this.documentsService.findDocument(docs, enumDocumentType.POWER_OF_ATTORNEY)),
    );
  }

  private findEntryPunDocument() {
    return this.entryDocuments$.pipe(
      take(1),
      map((docs) => this.documentsService.findDocument(docs, enumDocumentType.ACH_APPROVAL)),
    );
  }

  private createOrUpdateBondRequest(bondRequest: BondRequestUpdateModel): Observable<BondRequestModel> {
    const entry = this.entrySummary$.getValue();
    if (entry.bondRequest && entry.bondRequest.id && entry.bondRequest.state === enumBondRequestState.CREATED) {
      return this.bondRequestRepository.update(entry.bondRequest.id, bondRequest);
    }
    return this.bondRequestRepository.create(this.companiesService.getCompany().id, bondRequest);
  }

  private createDocsForLCL(entrySummary: EntrySummaryModel, invoiceDocument?: DocumentModel): Observable<DocumentModel> {
    return this.documentsService.copyCompanyPoaToEntry(entrySummary.id, enumDocumentType.POWER_OF_ATTORNEY).pipe(
      switchMap(() => {
        return !!invoiceDocument
          ? this.documentsService.copyInvoiceDocToEntry(
              entrySummary.id,
              entrySummary.shipments[0]?.invoices?.[0]?.id,
              invoiceDocument.id,
              invoiceDocument.fileName,
            )
          : of(null);
      }),
    );
  }

  private createLinesForInvoice(invoiceId: number, esData: LclEntrySummaryUpsertModel): Observable<any> {
    const apiCalls: Array<Observable<LineModel>> = [];
    esData.packages.forEach((item: PackageModel) => {
      apiCalls.push(
        this.lineItemsRepository.create(invoiceId, {
          countryOfOriginId: esData.voyage?.route?.departurePort?.country?.id,
          countryOfExportId: esData.voyage?.route?.departurePort?.country?.id,
          grossWeight: item.weightUnit === enumWeightUnit.KG ? item.weight : precisionRound(Number(item.weight / LB_TO_KG_RATIO)),
          quantity1: item.quantity,
        }),
      );
    });

    return forkJoin(apiCalls);
  }
}
